import Head from 'next/head';
import { useRouter } from 'next/router';
import React, { ReactElement, useEffect, useState } from 'react';

import { CustomLinksAttributesWithSelection } from 'lib/atoms/signUpSocietyAtom';
import { ONBOARDING_FLOW_STATE } from 'lib/consts/flowState';
import { withAuth } from 'lib/hocs/withAuth';
import { PAGE_ROUTES } from 'lib/page-routes';
import { useAuth } from 'lib/providers/AuthProvider';
import { fetchTranslationFiles, LOCALE_PAGES } from 'lib/services/page';

import styles from './Onboarding.module.scss';
import Step1 from './Step1';
import Step2 from './Step2';
import Step3 from './Step3';
import Step4 from './Step4';
import Welcome from './Welcome';

const Onboarding = (): ReactElement => {
  const router = useRouter();
  const { isManager, isLoggedIn, onUserLogout, managerSociety, manager } =
    useAuth();

  const [flowState, setFlowState] = useState<string>(
    manager?.flow_state || ONBOARDING_FLOW_STATE.PRE_COMPLETE
  );

  useEffect(() => {
    if (isLoggedIn && !isManager) {
      onUserLogout();
      router.replace(PAGE_ROUTES.SIGN_IN_SOCIETY);
    }
  }, [isLoggedIn, isManager]);

  const show_custom_link_screen =
    manager?.linktree_url !== null ||
    managerSociety?.custom_links?.data.length === 0;

  const stepStr = flowState?.match(/\d+/g);

  const step = stepStr ? parseInt(stepStr[0]) + 1 : 1;

  const progress = (step / (show_custom_link_screen ? 5 : 4)) * 100;

  useEffect(() => {
    if (flowState === ONBOARDING_FLOW_STATE.COMPLETE) {
      router.replace(PAGE_ROUTES.DASHBOARD);
    }
  }, [flowState]);

  const [customLink, setCustomLink] = React.useState<
    CustomLinksAttributesWithSelection[] | undefined
  >();

  return (
    <>
      <Head>
        <title>Onboarding | Huzzle</title>
        <meta name="description" content="Huzzle" />
      </Head>
      <section className={styles.Onboarding} data-testid="onboarding">
        {flowState === ONBOARDING_FLOW_STATE.PRE_COMPLETE && (
          <Welcome setFlowState={setFlowState} />
        )}

        {/* invite colleagues  */}
        {flowState === ONBOARDING_FLOW_STATE.POST_1 && (
          <Step1 progress={progress} setFlowState={setFlowState} />
        )}

        {/* link tree screen */}
        {flowState === ONBOARDING_FLOW_STATE.POST_2 && (
          <Step2
            progress={progress}
            setFlowState={setFlowState}
            customLink={customLink}
            setCustomLink={setCustomLink}
          />
        )}

        {/* complete company profile */}
        {flowState === ONBOARDING_FLOW_STATE.POST_3 && (
          <Step3 progress={progress} setFlowState={setFlowState} />
        )}

        {/* add events  */}
        {flowState === ONBOARDING_FLOW_STATE.POST_4 && (
          <Step4 progress={progress} setFlowState={setFlowState} />
        )}
      </section>
    </>
  );
};

export async function getStaticProps({ locale }: { locale: string }) {
  return {
    props: {
      ...(await fetchTranslationFiles({
        locale,
        pages: [LOCALE_PAGES.ONBOARDING_MANAGER],
      })),
    },
  };
}

const DashboardWrapper = withAuth(Onboarding);

export default DashboardWrapper;
